// sticky.ts
import type { Context } from '../types/context';
import { isMobile } from '../utils';
import type { DynamicStickyController, StickyOverrideConfig } from './sticky-dynamic';
import {
  initializeStickyHorizontal,
  initializeStickyMobile,
  initializeStickyVertical,
} from './sticky-dynamic';

// Extended type to support nested configurations for style-expand
type ExtendedStickyOverrideConfig = StickyOverrideConfig & {
  toolbar?: StickyOverrideConfig;
  panel?: StickyOverrideConfig;
  body?: StickyOverrideConfig;
};

// Type for storing controller instances
type StickyControllerInstance = {
  controller: DynamicStickyController;
  cleanup: () => void;
  layoutType: 'mobile' | 'vertical' | 'horizontal';
  layoutStyle: string;
  createdAt: number;
};

// Extend window type to include sticky instances storage
declare global {
  interface Window {
    __boostStickyInstances?: Map<string, StickyControllerInstance>;
  }
}

/** Small query helpers */
const qs = <T extends Element = HTMLElement>(sel: string, root: Document | Element = document) =>
  root.querySelector<T>(sel);

/**
 * Gets the global storage for sticky controller instances.
 * Creates it if it doesn't exist.
 *
 * @example
 * // Access controller instances from browser console:
 * const instances = window.__boostStickyInstances;
 *
 * // Get specific instance
 * const mobileInstance = Array.from(instances.values())
 *   .find(inst => inst.layoutType === 'mobile');
 *
 * // Access controller methods
 * mobileInstance?.controller.updateConfig({ debug: true });
 * mobileInstance?.controller.forceRefresh();
 */
function getStickyInstancesStorage(): Map<string, StickyControllerInstance> {
  if (!window.__boostStickyInstances) {
    window.__boostStickyInstances = new Map();
  }
  return window.__boostStickyInstances;
}

/**
 * Generates a unique key for storing controller instances.
 */
function getInstanceKey(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string
): string {
  return `${layoutType}-${layoutStyle}`;
}

/**
 * Stores a sticky controller instance globally.
 * Automatically cleans up any existing instance with the same key.
 */
function storeStickyInstance(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string,
  controller: DynamicStickyController,
  cleanup: () => void
): void {
  const storage = getStickyInstancesStorage();
  const key = getInstanceKey(layoutType, layoutStyle);

  // Cleanup existing instance if present
  const existing = storage.get(key);
  if (existing) {
    try {
      existing.cleanup();
    } catch (error) {
      console.warn(`[sticky] Error cleaning up existing instance for ${key}:`, error);
    }
  }

  // Store new instance
  storage.set(key, {
    controller,
    cleanup,
    layoutType,
    layoutStyle,
    createdAt: Date.now(),
  });
}

/**
 * Retrieves a sticky controller instance by layout type and style.
 */
export function getStickyInstance(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string
): StickyControllerInstance | undefined {
  const storage = getStickyInstancesStorage();
  const key = getInstanceKey(layoutType, layoutStyle);
  return storage.get(key);
}

/**
 * Gets all active sticky controller instances.
 */
export function getAllStickyInstances(): StickyControllerInstance[] {
  const storage = getStickyInstancesStorage();
  return Array.from(storage.values());
}

/**
 * Cleans up a specific sticky instance by layout type and style.
 */
export function cleanupStickyInstance(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string
): void {
  const storage = getStickyInstancesStorage();
  const key = getInstanceKey(layoutType, layoutStyle);
  const instance = storage.get(key);

  if (instance) {
    try {
      instance.cleanup();
    } catch (error) {
      console.warn(`[sticky] Error cleaning up instance for ${key}:`, error);
    }
    storage.delete(key);
  }
}

/**
 * Cleans up all sticky controller instances.
 */
export function cleanupAllStickyInstances(): void {
  const storage = getStickyInstancesStorage();

  for (const [key, instance] of storage.entries()) {
    try {
      instance.cleanup();
    } catch (error) {
      console.warn(`[sticky] Error cleaning up instance for ${key}:`, error);
    }
  }

  storage.clear();
}

export const getFilterSettings = (context) => {
  if (!context) {
    console.error('context getFilterSettings null');
    return {};
  }

  // swatch_settings get from filterSettings app admin
  return {
    ...(context?.templateSettings?.filterSettings || {}),
    swatch_settings: context?.app?.filterSettings?.swatch_settings,
  };
};

export const blockFilterOpening = (context) => {
  const {
    stickyFilterOnMobile,
    filterTreeMobileStyle,
    stickyFilterOnDesktop,
    filterLayout,
    filterTreeHorizontalStyle,
  } = getFilterSettings(context);
  if (!stickyFilterOnMobile && !stickyFilterOnDesktop) {
    return;
  }

  const isMobileSize = isMobile(context?.app?.generalSettings?.isTabletPortraitMax);
  const toggleButtonSelector = isMobileSize
    ? '.boost-sd__toolbar-top-mobile .boost-sd__filter-tree-toggle-button'
    : '.boost-sd__filter-tree-toggle-button--expand';

  const shouldApplyMobile = () => {
    return stickyFilterOnMobile && filterTreeMobileStyle === 'style1';
  };

  const shouldApplyDesktop = () => {
    return (
      stickyFilterOnDesktop &&
      filterLayout === 'horizontal' &&
      filterTreeHorizontalStyle === 'style-expand'
    );
  };

  const performToggleAction = (button: HTMLElement) => {
    if (shouldApplyDesktop() || shouldApplyMobile()) {
      if (button.classList.contains('boost-sd__filter-tree-toggle-button--active')) {
        document.body.classList.add('boost-sd__sticky-opening');
      } else {
        document.body.classList.remove('boost-sd__sticky-opening');
      }
    }
  };

  const checkAndPerformAction = () => {
    const filterTreeToggleButton = qs(toggleButtonSelector);
    if (filterTreeToggleButton) {
      performToggleAction(filterTreeToggleButton);
    }
  };

  // Initial action
  checkAndPerformAction();

  // Set up observer for the entire document to handle dynamic DOM changes
  if (shouldApplyDesktop() || shouldApplyMobile()) {
    // Clean up existing observer if any
    const windowWithObserver = window as Window & { _filterOpeningObserver?: MutationObserver };
    if (windowWithObserver._filterOpeningObserver) {
      windowWithObserver._filterOpeningObserver.disconnect();
    }

    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;

      mutations.forEach((mutation) => {
        // Check for added nodes that might contain our toggle button
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if the added node is our button or contains our button
              if (
                element.matches?.(toggleButtonSelector) ||
                element.querySelector?.(toggleButtonSelector)
              ) {
                shouldCheck = true;
                break;
              }
            }
          }
        }

        // Check for class changes on our target button
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.matches(toggleButtonSelector)
        ) {
          performToggleAction(mutation.target);
        }
      });

      // If new nodes were added that might affect our button, recheck
      if (shouldCheck) {
        checkAndPerformAction();
      }
    });

    // Observe both attribute changes and child list changes
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class'],
    });

    // Store observer on window for cleanup
    windowWithObserver._filterOpeningObserver = observer;
  }
};

export const cleanupFilterOpeningObserver = () => {
  const windowWithObserver = window as Window & { _filterOpeningObserver?: MutationObserver };

  if (windowWithObserver._filterOpeningObserver) {
    windowWithObserver._filterOpeningObserver.disconnect();
    delete windowWithObserver._filterOpeningObserver;
  }
};

export const getStickyStatus = (context: Context) => {
  const { stickyFilterOnMobile, stickyFilterOnDesktop } = getFilterSettings(context);
  return {
    stickyEnabled: stickyFilterOnDesktop || stickyFilterOnMobile,
    stickyFilterOnMobile,
    stickyFilterOnDesktop,
  };
};

/**
 * Merges global override with layout-specific override.
 * Layout-specific overrides take precedence over global ones.
 *
 * @param globalOverride - Global sticky override configuration
 * @param layoutSpecificOverride - Layout-specific sticky override configuration
 * @returns Merged override configuration
 */
export function mergeOverrides(
  globalOverride?: StickyOverrideConfig,
  layoutSpecificOverride?: StickyOverrideConfig
): StickyOverrideConfig | undefined {
  if (!globalOverride && !layoutSpecificOverride) {
    return undefined;
  }

  if (!globalOverride) {
    return layoutSpecificOverride;
  }

  if (!layoutSpecificOverride) {
    return globalOverride;
  }

  // Deep merge with layout-specific taking precedence
  return {
    elements: {
      ...globalOverride.elements,
      ...layoutSpecificOverride.elements,
    },
    styling: {
      ...globalOverride.styling,
      ...layoutSpecificOverride.styling,
    },
    behavior: {
      ...globalOverride.behavior,
      ...layoutSpecificOverride.behavior,
    },
    hooks: {
      ...globalOverride.hooks,
      ...layoutSpecificOverride.hooks,
    },
    debug: layoutSpecificOverride.debug ?? globalOverride.debug,
  };
}

// Helper function to get global and layout-specific sticky overrides
function getConfigOverrides(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string
): {
  globalOverride: StickyOverrideConfig | undefined;
  layoutSpecificOverride: StickyOverrideConfig | undefined;
} {
  const config = window.boostWidgetIntegration?.config?.customization?.filter?.sticky;
  const globalOverride = config?.global;
  const layoutSpecificOverride = config?.[layoutType]?.[layoutStyle];
  return { globalOverride, layoutSpecificOverride };
}

/**
 * Generic helper function to get merged sticky configuration for any layout type.
 *
 * @param layoutType - The layout type (mobile, vertical, horizontal)
 * @param layoutStyle - The specific style within that layout
 * @returns Object containing merged configuration and individual configs for nested elements
 */
export function getStickyConfig(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string
) {
  // Use helper to get overrides
  const { globalOverride, layoutSpecificOverride } = getConfigOverrides(layoutType, layoutStyle);

  // Merge global override with layout-specific override (layout-specific takes precedence)
  const mergedOverride = mergeOverrides(globalOverride, layoutSpecificOverride);

  // Cast to extended type to access nested properties
  const extendedOverride = mergedOverride as ExtendedStickyOverrideConfig;

  return {
    merged: mergedOverride,
    // For horizontal functions, we might have nested configurations
    panel: extendedOverride?.panel || mergedOverride,
    content: extendedOverride?.body || mergedOverride,
    // For vertical functions
    toolbar: extendedOverride?.toolbar || mergedOverride,
  };
}

export const stickyMobile = async (context: Context) => {
  const { filterTreeMobileStyle } = getFilterSettings(context);

  // Use getStickyConfig helper to get merged configuration
  const { merged } = getStickyConfig('mobile', filterTreeMobileStyle);

  const init = initializeStickyMobile(context, merged);
  let result;

  switch (filterTreeMobileStyle) {
    case 'style1':
      result = await init.loadStickyFilterTreeMobileExpand();
      break;
    default:
      result = await init.loadStickyFilterTreeMobile();
      break;
  }

  // Store instance globally if controller was created
  if (result?.controller) {
    storeStickyInstance('mobile', filterTreeMobileStyle, result.controller, result.cleanup);
  }

  return result;
};

export const stickyVertical = async (context: Context) => {
  const { filterTreeVerticalStyle } = getFilterSettings(context);

  // Use getStickyConfig helper to get merged configuration
  const { merged, toolbar } = getStickyConfig('vertical', filterTreeVerticalStyle);

  const init = initializeStickyVertical(context, merged);
  let result;

  switch (filterTreeVerticalStyle) {
    case 'style-default': {
      result = await init.loadStickyFilterTree();
      break;
    }
    case 'style-expand': {
      // For style-expand, use the toolbar-specific config if available
      result = await init.loadStickyFilterTreeExpand(toolbar);
      break;
    }
    default: {
      result = await init.loadStickyFilterTreeOffCanvas();
      break;
    }
  }

  // Store instance globally if controller was created
  if (result?.controller) {
    storeStickyInstance('vertical', filterTreeVerticalStyle, result.controller, result.cleanup);
  }

  return result;
};

export const stickyHorizontal = async (context: Context) => {
  const { filterTreeHorizontalStyle } = getFilterSettings(context);

  // Use getStickyConfig helper to get merged configuration
  const { merged, toolbar } = getStickyConfig('horizontal', filterTreeHorizontalStyle);

  const init = initializeStickyHorizontal(context, merged);
  let result;

  switch (filterTreeHorizontalStyle) {
    case 'style-expand': {
      // For style-expand, use the toolbar-specific config if available
      result = await init.loadStickyFilterTreeHorizontalToolbar(toolbar);
      break;
    }
    default: {
      result = await init.loadStickyFilterTreeHorizontal();
      break;
    }
  }

  // Store instance globally if controller was created
  if (result?.controller) {
    storeStickyInstance('horizontal', filterTreeHorizontalStyle, result.controller, result.cleanup);
  }

  return result;
};

export * from './sticky-dynamic';
